// src/components/contacts/ContactDetailSlideOver.tsx
// Contact detail and edit slide-over panel

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ContactAssignedProvidersSection from '../providerAssignments/ContactAssignedProvidersSection';
import SubjectComplianceSection from '../compliance/SubjectComplianceSection';
import { useHasCapability } from '../../services/centralContext';
import { useGetContactQuery, useUpdateContactMutation } from '../../api/contactsApi';
import { useGetTasksQuery } from '../../api/tasksApi';
import { useGetOpportunitiesQuery } from '../../api/opportunitiesApi';
import { useGetThreadsByContactQuery, useGetOrCreateUnifiedThreadMutation } from '../../api/uniboxApi';
import { useSendSmsMutation } from '../../api/telephonyApi';
import { useTelephonyAvailability } from '../../hooks/useTelephonyAvailability';
import { useTwilioVoiceDevice } from '../../hooks/useTwilioVoiceDevice';
import SlideOver from '../ui/SlideOver';
import Button from '../ui/Button';
import ConfirmDialog from '../ui/ConfirmDialog';
import LoadingSpinner from '../common/LoadingSpinner';
import ContactAISummary from './ContactAISummary';
// Outbound Contact Workspace panel — read-only, feature-flag gated.
// See OutboundContactWorkspacePanel.tsx for the strict rules that apply
// when this component is mounted.
import OutboundContactWorkspacePanel from './OutboundContactWorkspacePanel';
import ContactCirclesSection from '../circles/ContactCirclesSection';
import ContactCompaniesSection from './ContactCompaniesSection';
import { useContactPermissions } from '../../hooks/useContactCompanyPermissions';
import type { Contact, CommunicationThread } from '../../types/api';

const OUTBOUND_WORKSPACE_ENABLED =
  import.meta.env.VITE_OUTBOUND_CONTACT_WORKSPACE === 'true';

const CONTACT_TYPES = ['lead', 'client', 'vendor', 'partner', 'team', 'other'];

/**
 * The fields the edit form owns. Deliberately narrower than `Partial<Contact>`:
 * it excludes `doNotContact`, so an ordinary contact save can never carry that
 * flag (in either direction) into the PATCH body. DNC is raised only by its own
 * one-way action — see `handleMarkDoNotContact`.
 */
type ContactEditFields = Pick<
  Contact,
  'fullName' | 'email' | 'phone' | 'company' | 'title' | 'type' | 'notes'
>;

interface ContactDetailSlideOverProps {
  contactId: string | null;
  workspaceId: string;
  onClose: () => void;
}

export default function ContactDetailSlideOver({
  contactId,
  workspaceId,
  onClose,
}: ContactDetailSlideOverProps) {
  // Capability-driven, never role-driven. False while the Central context is
  // unresolved, so no mutation control flashes enabled during boot.
  const { canEditContact } = useContactPermissions();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<ContactEditFields>>({});
  const [showSmsCompose, setShowSmsCompose] = useState(false);
  const [smsBody, setSmsBody] = useState('');
  const [smsBanner, setSmsBanner] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [callBanner, setCallBanner] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showDncConfirm, setShowDncConfirm] = useState(false);
  const [isMarkingDnc, setIsMarkingDnc] = useState(false);
  const [dncError, setDncError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [sendSms, { isLoading: isSendingSms }] = useSendSmsMutation();
  const [getOrCreateUnifiedThread, { isLoading: isCreatingUnified }] = useGetOrCreateUnifiedThreadMutation();
  const {
    isPhoneAvailable,
    isSmsAvailable,
    canMakeCalls,
    canSendSms,
  } = useTelephonyAvailability();

  // Phase 3 — calls route through the browser VoIP device, NOT the
  // server-bridge mutation. Same flow used on /phone and on contact list
  // rows so the user has one consistent calling experience.
  const {
    isReady: isVoiceReady,
    activeCall,
    connectToNumber,
    status: voiceStatus,
  } = useTwilioVoiceDevice();
  const callInProgress =
    activeCall.kind === 'connecting' || activeCall.kind === 'in_call';

  // STRICT TWILIO RULE: button is enabled only when there is a connected
  // Twilio integration AND a usable Twilio number AND the user has the
  // capability AND the browser voice device is ready AND no call is in
  // progress.
  const callButtonDisabled =
    !isPhoneAvailable || !canMakeCalls || !isVoiceReady || callInProgress;
  const smsButtonDisabled = !isSmsAvailable || !canSendSms || isSendingSms;

  // Spec-aligned wording — same string the server returns in the
  // TWILIO_NOT_CONFIGURED / NO_PHONE_NUMBER response messages so the user
  // sees identical copy whether the gate fails client-side or server-side.
  const TWILIO_UNAVAILABLE_CALL =
    'Calling is unavailable. Twilio is not configured or no number is assigned.';
  const TWILIO_UNAVAILABLE_SMS =
    'SMS is unavailable. Twilio is not configured or no number is assigned.';

  const callButtonTitle = !isPhoneAvailable
    ? TWILIO_UNAVAILABLE_CALL
    : !canMakeCalls
    ? 'You do not have permission to make calls'
    : 'Call contact';
  const smsButtonTitle = !isSmsAvailable
    ? TWILIO_UNAVAILABLE_SMS
    : !canSendSms
    ? 'You do not have permission to send SMS'
    : 'Send SMS';

  const {
    data: contact,
    isLoading,
    error,
    refetch: refetchContact,
  } = useGetContactQuery(contactId!, { skip: !contactId });

  const [updateContact, { isLoading: isSaving }] = useUpdateContactMutation();
  // Holds the spinner on "Save Changes" through both the PATCH and the
  // detail refetch that follows — RTK Query's `isSaving` alone clears
  // when the mutation resolves, before the detail view rerenders.
  const [isPersistingEdit, setIsPersistingEdit] = useState(false);

  // Fetch related tasks for this contact using server-side filter
  const { data: tasksResponse } = useGetTasksQuery(
    { workspaceId, relatedContactId: contactId! },
    { skip: !workspaceId || !contactId }
  );
  const relatedTasks = tasksResponse?.tasks || [];

  // Fetch related opportunities for this contact
  const { data: opportunities } = useGetOpportunitiesQuery(
    { workspaceId },
    { skip: !workspaceId || !contactId }
  );

  // Fetch communication threads for this contact (uses dedicated contact endpoint)
  const { data: threadsData, isLoading: isLoadingThreads } = useGetThreadsByContactQuery(
    { contactId: contactId!, limit: 10 },
    { skip: !contactId }
  );

  // Filter opportunities related to this contact (still client-side - no server filter available)
  const relatedOpportunities = opportunities?.filter((o) => o.contactId === contactId) || [];

  // Helper to format thread date
  const formatThreadDate = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Initialize form data when contact loads
  useEffect(() => {
    if (contact) {
      setFormData({
        fullName: contact.fullName,
        email: contact.email,
        phone: contact.phone,
        company: contact.company,
        title: contact.title,
        type: contact.type,
        notes: contact.notes,
      });
    }
  }, [contact]);

  const handleSave = async () => {
    if (!contactId) return;

    setIsPersistingEdit(true);
    try {
      // The mutation's `onQueryStarted` optimistically merges these
      // fields into the cached single-contact + list entries, so the
      // slide-over and table update the moment the PATCH round-trip
      // completes — the tag-invalidation refetch reconciles later in
      // the background.
      // GATE B5: `company` is a derived cache of the primary affiliation and is
      // no longer editable here, so it is stripped from the payload. Sending it
      // back would let a stale form value overwrite a value the affiliation
      // service owns — reintroducing exactly the divergence B1-B4 eliminated.
      const { company: _derivedCompany, ...editableFields } = formData;

      await updateContact({
        id: contactId,
        ...editableFields,
      }).unwrap();
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update contact:', error);
    } finally {
      setIsPersistingEdit(false);
    }
  };

  const handleCancel = () => {
    if (contact) {
      setFormData({
        fullName: contact.fullName,
        email: contact.email,
        phone: contact.phone,
        company: contact.company,
        title: contact.title,
        type: contact.type,
        notes: contact.notes,
      });
    }
    setIsEditing(false);
  };

  /**
   * Mark this contact as Do Not Contact.
   *
   * UPGRADE-ONLY: the request carries `doNotContact: true` and nothing else, so
   * an ordinary field edit can never imply a downgrade. `doNotContact` is
   * deliberately absent from `formData` for the same reason. There is no
   * counterpart action — the flag cannot be cleared from this UI, and the
   * server ignores a `false` regardless.
   */
  const handleMarkDoNotContact = async () => {
    if (!contactId || contact?.doNotContact) return;

    setDncError(null);
    setIsMarkingDnc(true);
    try {
      await updateContact({ id: contactId, doNotContact: true }).unwrap();
      setShowDncConfirm(false);
    } catch (err) {
      console.error('Failed to mark contact as Do Not Contact:', err);
      setDncError('Could not mark this contact as Do Not Contact. Please try again.');
    } finally {
      setIsMarkingDnc(false);
    }
  };

  /**
   * Map an RTK Query error from the telephony API into a user-facing
   * message. Handles the strict TWILIO_NOT_CONFIGURED / NO_PHONE_NUMBER
   * codes by surfacing the spec wording so the banner matches the
   * disabled-button tooltip exactly.
   */
  const mapTelephonyError = (
    err: unknown,
    fallback: string,
    twilioFallback: string
  ): string => {
    if (err && typeof err === 'object' && 'data' in err) {
      const d = (err as { data?: { code?: string; message?: string; error?: string } }).data;
      if (d?.code === 'TWILIO_NOT_CONFIGURED' || d?.code === 'NO_PHONE_NUMBER') {
        return twilioFallback;
      }
      return d?.message || d?.error || fallback;
    }
    return fallback;
  };

  // Pre-call recording toggle removed — the mid-call InCallRecordingControl
  // (inside ActiveCallShell / drawer) drives recording after answer. This
  // surface no longer owns a per-contact recording preference.
  void voiceStatus;

  const handleCall = async () => {
    setCallBanner(null);
    if (!contact?.phone) return;
    try {
      await connectToNumber(contact.phone, {
        contactId: contactId || undefined,
        contactName: contact.fullName,
      });
      setCallBanner({ type: 'success', message: `Calling ${contact.fullName}` });
      setTimeout(() => setCallBanner(null), 5000);
    } catch (err) {
      setCallBanner({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to start call.',
      });
    }
  };

  const handleSendSms = async () => {
    if (!contact?.phone || !smsBody.trim()) return;
    setSmsBanner(null);
    try {
      await sendSms({
        toNumber: contact.phone,
        body: smsBody.trim(),
        contactId: contactId || undefined,
      }).unwrap();
      setSmsBanner({ type: 'success', message: 'SMS sent' });
      setSmsBody('');
      setShowSmsCompose(false);
      setTimeout(() => setSmsBanner(null), 5000);
    } catch (err) {
      setSmsBanner({
        type: 'error',
        message: mapTelephonyError(
          err,
          'Failed to send SMS',
          'SMS is unavailable. Twilio is not configured or no number is assigned.'
        ),
      });
    }
  };

  /**
   * Open the unified conversation for this contact. Creates the unified
   * thread if it doesn't exist yet (idempotent). Navigates to the Unibox
   * with the thread selected and optionally preselects a send medium.
   *
   * @param preselectedMedium  - 'sms' | 'email' | undefined
   */
  const handleOpenConversation = useCallback(async (preselectedMedium?: 'sms' | 'email') => {
    if (!contactId) return;
    try {
      const result = await getOrCreateUnifiedThread({ contactId }).unwrap();
      const params = new URLSearchParams({ threadId: result.id });
      if (preselectedMedium) params.set('medium', preselectedMedium);
      onClose(); // Close the slide-over before navigating
      navigate(`/messages?${params.toString()}`);
    } catch (err) {
      console.error('Failed to open unified thread:', err);
    }
  }, [contactId, getOrCreateUnifiedThread, navigate, onClose]);

  return (
    <SlideOver
      isOpen={!!contactId}
      onClose={onClose}
      title={isEditing ? 'Edit Contact' : 'Contact Details'}
      size="lg"
      footer={
        isEditing ? (
          <>
            <Button variant="ghost" onClick={handleCancel} disabled={isSaving || isPersistingEdit}>
              Cancel
            </Button>
            <Button onClick={handleSave} isLoading={isSaving || isPersistingEdit}>
              Save Changes
            </Button>
          </>
        ) : (
          // GATE B5 / Phase 17: this surface now gates affiliation mutations on
          // contacts.write, so leaving Edit Contact ungated would make the same
          // panel internally inconsistent — a viewer would be denied one control
          // and offered another that predictably 403s.
          canEditContact ? (
            <Button onClick={() => setIsEditing(true)}>Edit Contact</Button>
          ) : null
        )
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-red-500">Failed to load contact details.</p>
        </div>
      ) : contact ? (
        <div className="space-y-6">
          {/* Contact Avatar & Name */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-2xl font-semibold text-blue-600">
                {contact.fullName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.fullName || ''}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="text-xl font-semibold text-gray-900 px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <h3 className="text-xl font-semibold text-gray-900">{contact.fullName}</h3>
              )}
              {contact.title && !isEditing && (
                <p className="text-gray-500">{contact.title}</p>
              )}
            </div>
          </div>

          {/* Quick Actions — open unified conversation */}
          {!isEditing && contactId && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleOpenConversation()}
                disabled={isCreatingUnified}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {isCreatingUnified ? 'Opening...' : 'Message'}
              </button>
              {contact.phone && canSendSms && (
                <button
                  onClick={() => handleOpenConversation('sms')}
                  disabled={isCreatingUnified}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50"
                  title="Open conversation with SMS selected"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  SMS
                </button>
              )}
              {contact.email && (
                <button
                  onClick={() => handleOpenConversation('email')}
                  disabled={isCreatingUnified}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
                  title="Open conversation with Email selected"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email
                </button>
              )}
            </div>
          )}

          {/* Do Not Contact — one-way suppression.
              Raising this flag is permanent from the CRM UI: there is no
              clear action here and no DNC-lift event, so the control renders
              as a static badge once set rather than a toggle. */}
          {!isEditing && contactId && (
            <div>
              {contact.doNotContact ? (
                <div
                  data-testid="dnc-status"
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                  <span>Do Not Contact</span>
                  <span className="text-xs font-normal text-red-600">
                    — outreach is suppressed for this contact
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="dnc-mark-button"
                  onClick={() => {
                    setDncError(null);
                    setShowDncConfirm(true);
                  }}
                  disabled={isMarkingDnc}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
                  title="Mark this contact as Do Not Contact. This cannot be undone here."
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                  {isMarkingDnc ? 'Marking...' : 'Mark as Do Not Contact'}
                </button>
              )}
              {dncError && (
                <div
                  data-testid="dnc-error"
                  className="mt-2 p-2 rounded-md text-xs bg-red-50 text-red-700 border border-red-200"
                >
                  {dncError}
                </div>
              )}
            </div>
          )}

          {/* AI Communication Summary */}
          {!isEditing && contactId && (
            <ContactAISummary contactId={contactId} />
          )}

          {/* Outbound Contact Workspace — read-only Outbound-sourced
              research / brief / activity. Flag-gated so the panel is a
              no-op in envs where VITE_OUTBOUND_CONTACT_WORKSPACE isn't
              explicitly set to 'true'. */}
          {!isEditing && contactId && OUTBOUND_WORKSPACE_ENABLED && (
            <div className="mt-4">
              <OutboundContactWorkspacePanel crmContactId={contactId} />
            </div>
          )}

          {/* Core Fields */}
          <div className="grid grid-cols-1 gap-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              {isEditing ? (
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-gray-900">{contact.email || '-'}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              {isEditing ? (
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-gray-900">{contact.phone || '-'}</p>
                  {contact.phone && (
                    <>
                      <button
                        onClick={handleCall}
                        disabled={callButtonDisabled}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title={callButtonTitle}
                        aria-label="Call contact"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => setShowSmsCompose((v) => !v)}
                        disabled={smsButtonDisabled}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title={smsButtonTitle}
                        aria-label="Send SMS"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                          />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              )}
              {/* Pre-call recording toggle intentionally removed — the
                  mid-call InCallRecordingControl in the active-call
                  shell drives recording after the call connects. */}
              {/* Telephony banners */}
              {callBanner && (
                <div className={`mt-2 p-2 rounded-md text-xs ${callBanner.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {callBanner.message}
                </div>
              )}
              {smsBanner && (
                <div className={`mt-2 p-2 rounded-md text-xs ${smsBanner.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {smsBanner.message}
                </div>
              )}
              {showSmsCompose && contact.phone && (
                <div className="mt-2 space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <textarea
                    value={smsBody}
                    onChange={(e) => setSmsBody(e.target.value)}
                    placeholder="Type your SMS message..."
                    rows={3}
                    maxLength={1600}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    aria-label="SMS message body"
                  />
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-500">{smsBody.length}/1600</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setShowSmsCompose(false);
                          setSmsBody('');
                        }}
                        className="px-3 py-1 text-xs text-gray-600 hover:text-gray-900"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSendSms}
                        disabled={!smsBody.trim() || isSendingSms}
                        className="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSendingSms ? 'Sending...' : 'Send SMS'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Company & Title */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary company
                </label>
                {/*
                  GATE B5 — this field is now READ-ONLY and DERIVED.

                  `contacts.company` is a compatibility cache of the PRIMARY
                  affiliation, written solely by the affiliation service. Leaving
                  it free-text would create a second editable source of truth that
                  silently diverges from the real relationship — the exact failure
                  B1-B4 removed on the server.

                  Structured linking lives in the Companies section below; that is
                  where the primary is chosen. Legacy text on contacts with no
                  Company record is preserved and still displayed — nothing is
                  erased — it simply can no longer be edited into divergence.
                */}
                <p className="text-gray-900">{contact.company || '-'}</p>
                {isEditing && (
                  <p className="mt-1 text-xs text-gray-500">
                    Set from the primary company below.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.title || ''}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                ) : (
                  <p className="text-gray-900">{contact.title || '-'}</p>
                )}
              </div>
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              {isEditing ? (
                <select
                  value={formData.type || ''}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select type...</option>
                  {CONTACT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="inline-flex px-2 py-1 text-sm font-medium bg-gray-100 text-gray-700 rounded-full capitalize">
                  {contact.type || 'Not set'}
                </span>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              {isEditing ? (
                <textarea
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-gray-900 whitespace-pre-wrap">{contact.notes || 'No notes'}</p>
              )}
            </div>
          </div>

          {/* Related Items Section */}
          {!isEditing && (
            <div className="space-y-4 pt-4 border-t border-gray-200">
              {/* Related Tasks */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                  Related Tasks ({relatedTasks.length})
                </h4>
                {relatedTasks.length > 0 ? (
                  <div className="space-y-2">
                    {relatedTasks.slice(0, 5).map((task) => (
                      <div
                        key={task.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                      >
                        <span className="text-sm text-gray-900 truncate">{task.title}</span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            task.status === 'done'
                              ? 'bg-green-100 text-green-700'
                              : task.status === 'blocked'
                                ? 'bg-red-100 text-red-700'
                                : task.status === 'in_progress'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {task.status.replace('_', ' ')}
                        </span>
                      </div>
                    ))}
                    {relatedTasks.length > 5 && (
                      <p className="text-xs text-gray-500">
                        +{relatedTasks.length - 5} more tasks
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No related tasks</p>
                )}
              </div>

              {/* Companies (Gate B5) — the canonical affiliation surface */}
              <ContactCompaniesSection
                contactId={contact.id}
                contactName={contact.fullName ?? 'This contact'}
              />

              {/* Circles */}
              <ContactCirclesSection
                contactId={contact.id}
                contactName={contact.fullName}
                workspaceId={workspaceId}
              />

              {/* Related Opportunities */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                  Related Opportunities ({relatedOpportunities.length})
                </h4>
                {relatedOpportunities.length > 0 ? (
                  <div className="space-y-2">
                    {relatedOpportunities.slice(0, 5).map((opp) => (
                      <div
                        key={opp.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-gray-900 truncate block">
                            {opp.title}
                          </span>
                          {opp.amount && (
                            <span className="text-xs text-gray-500">
                              ${opp.amount.toLocaleString()}
                            </span>
                          )}
                        </div>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            opp.status === 'won'
                              ? 'bg-green-100 text-green-700'
                              : opp.status === 'lost'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {opp.status}
                        </span>
                      </div>
                    ))}
                    {relatedOpportunities.length > 5 && (
                      <p className="text-xs text-gray-500">
                        +{relatedOpportunities.length - 5} more opportunities
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No related opportunities</p>
                )}
              </div>

              {/* Assigned Providers */}
              {contactId && (
                <ContactAssignedProvidersSection
                  contactId={contactId}
                  workspaceId={workspaceId ?? undefined}
                />
              )}

              {/* Compliance */}
              <ContactComplianceBlock
                contactId={contactId}
                workspaceId={workspaceId ?? null}
              />


              {/* Communication Timeline */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Communication ({threadsData?.threads.length || 0})
                </h4>
                {isLoadingThreads ? (
                  <div className="flex justify-center py-4">
                    <LoadingSpinner size="sm" />
                  </div>
                ) : threadsData?.threads && threadsData.threads.length > 0 ? (
                  <div className="space-y-2">
                    {threadsData.threads.map((thread) => (
                      <button
                        key={thread.id}
                        onClick={() => {
                          onClose();
                          navigate(`/messages?threadId=${thread.id}`);
                        }}
                        className="w-full text-left flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      >
                        {/* Medium Icon */}
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          (thread as any).threadType === 'unified'
                            ? 'bg-purple-100 text-purple-600'
                            : thread.primaryMedium === 'sms'
                            ? 'bg-green-100 text-green-600'
                            : 'bg-blue-100 text-blue-600'
                        }`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d={thread.primaryMedium === 'sms'
                                ? 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z'
                                : 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                              }
                            />
                          </svg>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                (thread as any).threadType === 'unified'
                                  ? 'bg-purple-100 text-purple-700'
                                  : thread.primaryMedium === 'sms'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {(thread as any).threadType === 'unified' ? 'Unified' : thread.primaryMedium === 'sms' ? 'SMS' : 'Email'}
                              </span>
                              {thread.unreadCount > 0 && (
                                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-blue-600 rounded-full">
                                  {thread.unreadCount}
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-400">
                              {formatThreadDate(thread.lastMessageAt)}
                            </span>
                          </div>
                          {thread.subject && (
                            <p className="text-sm font-medium text-gray-900 truncate mt-0.5">
                              {thread.subject}
                            </p>
                          )}
                          <p className="text-sm text-gray-500 truncate">
                            {thread.lastMessagePreview || 'No preview'}
                          </p>
                        </div>
                      </button>
                    ))}
                    {threadsData.pagination && threadsData.pagination.total > 10 && (
                      <p className="text-xs text-gray-500 text-center pt-1">
                        +{threadsData.pagination.total - 10} more conversations
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No communication history</p>
                )}
              </div>
            </div>
          )}

          {/* Metadata */}
          {!isEditing && (
            <div className="pt-4 border-t border-gray-200 text-xs text-gray-500">
              <p>Created: {new Date(contact.createdAt).toLocaleDateString()}</p>
              <p>Updated: {new Date(contact.updatedAt).toLocaleDateString()}</p>
            </div>
          )}
        </div>
      ) : null}

      {/*
        Phase 3: the call modal is gone — calls are placed via
        connectToNumber() which goes through the browser VoIP device.
        From-number selection is resolved server-side by
        resolveOutboundFromNumber so users do not pick a from-number
        on the contact action.
      */}

      <ConfirmDialog
        isOpen={showDncConfirm}
        title="Mark as Do Not Contact?"
        message="Outreach to this contact will be suppressed across the suite. This cannot be undone from the CRM."
        confirmLabel="Mark as Do Not Contact"
        variant="danger"
        onConfirm={handleMarkDoNotContact}
        onCancel={() => setShowDncConfirm(false)}
      />
    </SlideOver>
  );
}

// ---------------------------------------------------------------------------
// ContactComplianceBlock — capability-gated, workspace-scoped
// ---------------------------------------------------------------------------

function ContactComplianceBlock({
  contactId,
  workspaceId,
}: {
  contactId: string | null;
  workspaceId: string | null;
}) {
  const canView = useHasCapability('compliance', 'view');
  if (!canView || !contactId || !workspaceId) return null;
  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        Compliance
      </h4>
      <SubjectComplianceSection
        workspaceId={workspaceId}
        subjectType="contact"
        subjectId={contactId}
        compact
      />
    </div>
  );
}
